import type {
  ActiveMetric,
  CodeActivityDataPoint,
  LeaderboardEntry,
  LeaderboardMetricScore,
  LeaderboardSnapshot,
  ProgressionDataPoint,
  RadarDataPoint,
} from '@/sections/customerManagement/types';

const SEVERITY_KEYS = [
  'critical',
  'high',
  'medium',
  'low',
  'info',
] as const satisfies ReadonlyArray<keyof LeaderboardMetricScore>;

export function hasMetricBreakdownData(entry: LeaderboardEntry): boolean {
  return METRIC_DISPLAY_ORDER.some(name => {
    const metric = entry.metrics[name];
    return metric && SEVERITY_KEYS.some(k => (metric[k] ?? 0) > 0);
  });
}

export const METRIC_DISPLAY_ORDER = [
  'manual_discoveries',
  'automation_authored',
  'accurate_triage',
  'assists',
  'manual_to_automated',
  'code_commits',
  'cve_bounties',
  'auto_triage_rules',
  'effective_triage',
];

export const METRIC_DISPLAY_LABELS: Record<string, string> = {
  manual_discoveries: 'Manual',
  automation_authored: 'Automation',
  accurate_triage: 'Triage',
  assists: 'Assists',
  manual_to_automated: 'Conversions',
  code_commits: 'Code',
  cve_bounties: 'CVEs/Bounties',
  auto_triage_rules: 'Auto Rules',
  effective_triage: 'Eff. Triage',
};

export const METRIC_COLOR_CLASSES: Record<string, string> = {
  manual_discoveries: 'text-chariot-stroke-high',
  automation_authored: 'text-chariot-stroke-light-green',
  accurate_triage: 'text-chariot-stroke-blue',
  assists: 'text-chariot-stroke-cyan',
  manual_to_automated: 'text-chariot-stroke-magenta',
  code_commits: 'text-chariot-stroke-light-blue',
  cve_bounties: 'text-chariot-stroke-red',
  auto_triage_rules: 'text-chariot-stroke-medium',
  effective_triage: 'text-chariot-stroke-info',
};

export const METRIC_HEX_COLORS: Record<string, string> = {
  manual_discoveries: '#f0642b',
  automation_authored: '#22c55e',
  accurate_triage: '#2563eb',
  assists: '#06b6d4',
  manual_to_automated: '#d946ef',
  code_commits: '#60a5fa',
  cve_bounties: '#dc3232',
  auto_triage_rules: '#d4af37',
  effective_triage: '#28a36a',
};

const LABEL_TO_METRIC: Record<string, string> = {};
for (const [k, v] of Object.entries(METRIC_DISPLAY_LABELS)) {
  LABEL_TO_METRIC[v] = k;
}

export function getMetricHexByLabel(label: string): string {
  return METRIC_HEX_COLORS[LABEL_TO_METRIC[label] ?? ''] ?? '#888888';
}

export function getMetricColorClassByLabel(label: string): string {
  return (
    METRIC_COLOR_CLASSES[LABEL_TO_METRIC[label] ?? ''] ??
    'text-chariot-text-primary'
  );
}

export function getImprovementTip(displayLabel: string): string {
  const name = LABEL_TO_METRIC[displayLabel] ?? '';
  return METRIC_IMPROVEMENT_TIPS[name] ?? '';
}

export const METRIC_IMPROVEMENT_TIPS: Record<string, string> = {
  manual_discoveries:
    'Discover more vulnerabilities through manual testing. Focus on deep-dive assessments, business logic flaws, and complex attack chains that automated scanners miss.',
  automation_authored:
    'Author more automated detection rules (Nuclei templates, custom scripts). Each new template you publish increases your automation score.',
  accurate_triage:
    'Improve triage accuracy by carefully verifying findings before closing them. Fewer overridden triage decisions means a higher score.',
  assists:
    'Help other engineers by providing assists on their assessments — review findings, validate vulnerabilities, or pair on complex targets.',
  manual_to_automated:
    'Convert your manual findings into automated detection rules. When you discover a vulnerability pattern, write a Nuclei template or script that detects it automatically.',
  code_commits:
    'Contribute more code to the platform — bug fixes, feature additions, tooling improvements, and documentation all count toward your commit score.',
  cve_bounties:
    'Pursue CVE discoveries and bug bounty submissions. Responsibly disclose vulnerabilities in third-party software and register them as CVEs.',
  auto_triage_rules:
    'Create auto-triage rules that automatically categorize and prioritize findings. Rules that consistently match real patterns earn more credit.',
  effective_triage:
    'Review AI-triaged findings to earn severity-weighted points (Critical = 1, High = 0.4, Medium = 0.1, Low = 0.025). Focus on validating high-severity AI triage decisions for the biggest score impact.',
};

export const METRIC_CALCULATION_INFO: Record<
  string,
  { formula: string; example: string }
> = {
  manual_discoveries: {
    formula:
      'Sum of severity weights for each manually discovered vulnerability',
    example: '1 Critical + 2 High = 10 + 4 + 4 = 18 raw',
  },
  automation_authored: {
    formula:
      'Sum of severity weights for findings produced by templates you authored',
    example: '3 High findings from your template = 4 + 4 + 4 = 12 raw',
  },
  accurate_triage: {
    formula: '+1 per triage decision (Open or Dismiss) that is not overridden',
    example: '5 accepted triages + 2 dismissals = 7 raw',
  },
  assists: {
    formula:
      'Severity-weighted assists on other engineers’ findings + PR review scores',
    example: '1 High assist + 1 PR review (0.5) = 4 + 0.5 = 4.5 raw',
  },
  manual_to_automated: {
    formula:
      'Severity-weighted count of manual findings that also have an automated detection',
    example: '1 Critical converted = 10 raw',
  },
  code_commits: {
    formula: 'Sum of commit scores + FP-impact bonuses per merged PR',
    example: 'PR with score 2.0 + bonus 0.3 = 2.3 raw',
  },
  cve_bounties: {
    formula:
      'Severity weight × 0.1 per discovery + ⅓ per submission + ⅓ per publication',
    example:
      '1 High CVE discovered + submitted + published = 0.4 + 0.33 + 0.33 = 1.06 raw',
  },
  auto_triage_rules: {
    formula: '(Accurate AI triages − overridden) × your authorship multiplier',
    example: '10 accurate − 2 overridden = 8 × 1.0 = 8 raw',
  },
  effective_triage: {
    formula:
      'Severity weight × 0.1 per AI-triaged finding you review (C=1, H=0.4, M=0.1, L=0.025)',
    example: '2 Critical + 5 High AI triages reviewed = 2×1 + 5×0.4 = 4 raw',
  },
};

function snapshotToMetricsMap(
  snapshot: LeaderboardSnapshot
): Record<string, LeaderboardMetricScore> {
  const map: Record<string, LeaderboardMetricScore> = {};
  for (const m of snapshot.metrics) {
    map[m.metricName] = m;
  }
  return map;
}

function rankSnapshots(snapshots: LeaderboardSnapshot[]): Map<string, number> {
  const sorted = [...snapshots].sort((a, b) => b.totalScore - a.totalScore);
  const rankMap = new Map<string, number>();
  sorted.forEach((s, i) => rankMap.set(s.email, i + 1));
  return rankMap;
}

export function computeLeaderboardEntries(
  todaySnapshots: LeaderboardSnapshot[],
  yesterdaySnapshots: LeaderboardSnapshot[]
): LeaderboardEntry[] {
  const sorted = [...todaySnapshots].sort(
    (a, b) => b.totalScore - a.totalScore
  );
  const yesterdayRanks = rankSnapshots(yesterdaySnapshots);

  return sorted.map((snapshot, index) => {
    const todayRank = index + 1;
    const previousRank = yesterdayRanks.get(snapshot.email) ?? null;
    const rankDelta = previousRank !== null ? previousRank - todayRank : null;

    return {
      rank: todayRank,
      previousRank,
      rankDelta,
      email: snapshot.email,
      displayName: formatDisplayName(snapshot.email),
      totalScore: snapshot.totalScore,
      metrics: snapshotToMetricsMap(snapshot),
      capabilities: snapshot.capabilities,
    };
  });
}

export function getActiveMetrics(
  snapshots: LeaderboardSnapshot[]
): ActiveMetric[] {
  const weightByMetric = new Map<string, number>();

  for (const snapshot of snapshots) {
    for (const m of snapshot.metrics) {
      if (!weightByMetric.has(m.metricName)) {
        weightByMetric.set(m.metricName, m.weight);
      }
    }
  }

  return METRIC_DISPLAY_ORDER.map(name => ({
    metricName: name,
    displayLabel: METRIC_DISPLAY_LABELS[name] ?? name,
    weight: weightByMetric.get(name) ?? 0,
  }));
}

export function formatDisplayName(email: string): string {
  const local = email.split('@')[0] ?? email;
  return local
    .split(/[._-]/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function buildRadarData(
  userEntry: LeaderboardEntry,
  allEntries: LeaderboardEntry[],
  activeMetrics: ActiveMetric[]
): RadarDataPoint[] {
  return activeMetrics.map(metric => {
    const userRaw = userEntry.metrics[metric.metricName]?.rawValue ?? 0;
    const allValues = allEntries
      .map(e => e.metrics[metric.metricName]?.rawValue ?? 0)
      .filter(v => v > 0);

    const maxValue = Math.max(...allValues, 1);
    const percentile = (userRaw / maxValue) * 100;

    return {
      metric: metric.displayLabel,
      value: Math.round(percentile),
      rawValue: userRaw,
    };
  });
}

export function buildProgressionData(
  snapshotsByDay: { date: string; snapshots: LeaderboardSnapshot[] }[],
  userEmail: string
): ProgressionDataPoint[] {
  return snapshotsByDay
    .map(({ date, snapshots }) => {
      const ranks = rankSnapshots(snapshots);
      const userSnapshot = snapshots.find(s => s.email === userEmail);
      if (!userSnapshot) return null;

      const metrics: Record<string, number> = {};
      for (const m of userSnapshot.metrics) {
        metrics[m.metricName] = m.score;
      }

      return {
        date,
        totalScore: userSnapshot.totalScore,
        rank: ranks.get(userEmail) ?? 0,
        metrics,
      };
    })
    .filter((p): p is ProgressionDataPoint => p !== null);
}

export function buildCodeActivityData(
  snapshotsByDay: { date: string; snapshots: LeaderboardSnapshot[] }[],
  userEmail: string
): CodeActivityDataPoint[] {
  const cumulative = snapshotsByDay.map(({ date, snapshots }) => {
    const snap = snapshots.find(s => s.email === userEmail);
    if (!snap) return null;
    const adds =
      snap.metrics.find(m => m.metricName === 'code_additions')?.rawValue ?? 0;
    const dels =
      snap.metrics.find(m => m.metricName === 'code_deletions')?.rawValue ?? 0;
    return { date, additions: adds, deletions: dels };
  });

  const result: CodeActivityDataPoint[] = [];
  for (let i = 1; i < cumulative.length; i++) {
    const prev = cumulative[i - 1];
    const curr = cumulative[i];
    if (!prev || !curr) continue;
    result.push({
      date: curr.date,
      additions: Math.max(0, curr.additions - prev.additions),
      deletions: Math.max(0, curr.deletions - prev.deletions),
    });
  }
  return result;
}
